import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useTbdCheck from "../../../functions/useTbdCheck";
import useFieldClass from "../../../functions/useFieldClass";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { setPriceBreakdownDetails } from "../../../redux/slices/psnSlice";
import { selectPriceBreakdown, selectFieldDescriptions } from "../../../redux/selectors";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

function PriceBreakdown({ zone }) {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "priceBreakdown";

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;
  
  const error = null;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const storedJobDetails = useSelector(selectPriceBreakdown);

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const jobDetails = useMemo(() => storedJobDetails[0] || {}, [storedJobDetails]);

  const { formData, handleChange, setFormData } = useFormState({}); // Form state management

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  useEffect(() => {
    if (jobDetails) {
      dispatch(setPriceBreakdownDetails(jobDetails));
      setFormData({
        ID: jobDetails.ID || "",
        sellPriceAmount: jobDetails.SellPriceAmount || "",
        market: jobDetails.Market || "",
        freightAmount: jobDetails.FreightAmount || "",
        freightPayTo: jobDetails.FreightPayTo || "",
        freightProviderQuote: jobDetails.FreightProviderQuote || "",
        rebateAmount: jobDetails.RebateAmount || "",
        rebatePayTo: jobDetails.RebatePayTo || "",
        commission1Amount: jobDetails.Commission1Amount || "",
        commission1PayTo: jobDetails.Commission1PayTo || "",
        commission2Amount: jobDetails.Commission2Amount || "",
        commission2PayTo: jobDetails.Commission2PayTo || "",
        commission3Amount: jobDetails.Commission3Amount || "",
        commission3PayTo: jobDetails.Commission3PayTo || "",
        setupAmount: jobDetails.SetupAmount || "",
        setupPayTo: jobDetails.SetupPayTo || "",
        setupProviderQuote: jobDetails.SetupProviderQuote || "",
        startAmount: jobDetails.StartAmount || "",
        startPayTo: jobDetails.StartPayTo || "",
        startProviderQuote: jobDetails.StartProviderQuote || "",
        craneAmount: jobDetails.CraneAmount || "",
        cranePayTo: jobDetails.CranePayTo || "",
        craneProviderQuote: jobDetails.CraneProviderQuote || "",
        inspectionAmount: jobDetails.InspectionAmount || "",
        inspectionPayTo: jobDetails.InspectionPayTo || "",
        inspectionProviderQuote: jobDetails.InspectionProviderQuote || "",
        warranty: jobDetails.Warranty || "",
        warrantyAdditionalCost: jobDetails.WarrantyAdditionalCost || "",
        otherAmount: jobDetails.OtherAmount || "",
        otherPayTo: jobDetails.OtherPayTo || "",
        otherProviderQuote: jobDetails.OtherProviderQuote || "",
        netRevenue: jobDetails.NetRevenue || "",
        otherEstimatedFreight: jobDetails.OtherEstimatedFreight || "",
        otherEstimatedRental: jobDetails.OtherEstimatedRental || "",
        otherEstimatedLocalLabor: jobDetails.OtherEstimatedLocalLabor || "",
        otherEstimatedOther: jobDetails.OtherEstimatedOther || "",
        taxes: jobDetails.Taxes || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });
  const canEditPriceTerms = roles.Admin || roles.Apps || roles.PSN;

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const { tbdFields } = useTbdCheck(formData, zone); // Pass zone to track TBD status for specific zone
  const zoneTbdFields = zone ? tbdFields?.[zone] : tbdFields;

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  /****************Add dropdown options here*************************/
  const marketOptions =
    options?.filter((option) => option.DropdownType === "Market")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /***************************************************************** */
  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const formatAsCurrency = (value) => {
    if (value || value === 0) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(value);
    }
    return value;
  };

  const [editingField, setEditingField] = React.useState(null);
  
  const sanitizeInput = (input) => {
    // Remove any characters that are not digits, decimal points, or minus signs
    return input.replace(/[^0-9.-]/g, "");
  };

  const handleCurrencyBlur = (e, handleBlur, formData, apiUrl, tableKey, fieldName) => {
    // Parse and ensure the value is a number
    const sanitizedValue = sanitizeInput(e.target.value);
    const rawValue = parseFloat(sanitizedValue);
    if (!isNaN(rawValue)) {
      // Explicitly send a number to the backend
      handleBlur(
        { ...e, target: { ...e.target, value: String(rawValue) } }, // Ensure value is a number
        fieldName,
        [],
        {
          ID: formData.ID,
          url: apiUrl,
          field: fieldName,
          tableKey,
        }
      );
    } else {
      console.warn("Invalid numeric value provided, skipping submission.");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div>
            <Form onSubmit={handleSubmit} className="price-breakdown-form">
            <Row className="price-breakdown-table">
              <Col className="price-breakdown-table-body">
                <Row className="price-breakdown-column-headings mb-3">
                  <Col column="md" sm={1} />
                  <Col>
                    <h4 className="subsection-header">Amount</h4>
                  </Col>
                  <Col>
                    <h4 className="subsection-header">Pay To / Terms</h4>
                  </Col>
                  <Col>
                    <h4 className="subsection-header">Provider Quote</h4>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SellPriceAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Sell Price</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="sellPriceAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="sellPriceAmount"
                          value={
                            editingField === "sellPriceAmount"
                              ? formData.sellPriceAmount
                              : formatAsCurrency(formData.sellPriceAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("sellPriceAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.sellPriceAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "SellPriceAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={
                            zoneTbdFields?.sellPriceAmount ? "tbd-highlight" : ""
                          }
                          placeholder="Enter Sell Price Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="market">
                      <Form.Label column="md" sm={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("Market")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Market</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col sm={9}>
                        <Form.Select
                          name="market"
                          value={formData.market || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Market", marketOptions, {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "Market",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.market}
                          onChange={handleChange}
                        >
                          <option value="">Select Market</option>
                          {marketOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(formData.market, marketOptions) &&
                            formData.market && (
                              <option value={formData.market} style={{ color: "red" }}>
                                *{formData.market}
                              </option>
                            )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col />
                </Row>
                <Row className="even-row mb-3">
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("FreightAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Freight</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="freightAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="freightAmount"
                          value={
                            editingField === "freightAmount"
                              ? formData.freightAmount
                              : formatAsCurrency(formData.freightAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("freightAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.freightAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "FreightAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.freightAmount ? "tbd-highlight" : ""}
                          placeholder="Enter Freight Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="freightPayTo">
                      <Form.Control
                        type="text"
                        name="freightPayTo"
                        value={formData.freightPayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Freight Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "FreightPayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.freightPayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Freight Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="freightProviderQuote">
                      <Form.Control
                        type="text"
                        name="freightProviderQuote"
                        value={formData.freightProviderQuote}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Freight Provider Quote", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "FreightProviderQuote",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.freightProviderQuote
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Freight Provider Quote"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("RebateAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Rebate Amount</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="rebateAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="rebateAmount"
                          value={
                            editingField === "rebateAmount"
                              ? formData.rebateAmount
                              : formatAsCurrency(formData.rebateAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("rebateAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.rebateAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "RebateAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.rebateAmount ? "tbd-highlight" : ""}
                          placeholder="Enter Rebate Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="rebatePayTo">
                      <Form.Control
                        type="text"
                        name="rebatePayTo"
                        value={formData.rebatePayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Rebate Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "RebatePayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.rebatePayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Rebate Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col />
                </Row>
                <Row className="even-row mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Commission1Amount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Commission 1</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="commission1Amount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="commission1Amount"
                          value={
                            editingField === "commission1Amount"
                              ? formData.commission1Amount
                              : formatAsCurrency(formData.commission1Amount)
                          }
                          onFocus={(e) => {
                            setEditingField("commission1Amount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.commission1Amount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "Commission1Amount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.commission1Amount ? "tbd-highlight" : ""}
                          placeholder="Enter Commission 1 Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="commission1PayTo">
                      <Form.Control
                        type="text"
                        name="commission1PayTo"
                        value={formData.commission1PayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Commission 1 Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "Commission1PayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.commission1PayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Commission 1 Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col />
                </Row>
                <Row className="mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Commission2Amount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Commission 2</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="commission2Amount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="commission2Amount"
                          value={
                            editingField === "commission2Amount"
                              ? formData.commission2Amount
                              : formatAsCurrency(formData.commission2Amount)
                          }
                          onFocus={(e) => {
                            setEditingField("commission2Amount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.commission2Amount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "Commission2Amount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.commission2Amount ? "tbd-highlight" : ""}
                          placeholder="Enter Commission 2 Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="commission2PayTo">
                      <Form.Control
                        type="text"
                        name="commission2PayTo"
                        value={formData.commission2PayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Commission 2 Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "Commission2PayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.commission2PayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Commission 2 Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col />
                </Row>
                <Row className="even-row mb-3">
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Commission3Amount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Commission 3</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="commission3Amount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="commission3Amount"
                          value={
                            editingField === "commission3Amount"
                              ? formData.commission3Amount
                              : formatAsCurrency(formData.commission3Amount)
                          }
                          onFocus={(e) => {
                            setEditingField("commission3Amount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.commission3Amount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "Commission3Amount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.commission3Amount ? "tbd-highlight" : ""}
                          placeholder="Enter Commission 3 Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="commission3PayTo">
                      <Form.Control
                        type="text"
                        name="commission3PayTo"
                        value={formData.commission3PayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Commission 3 Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "Commission3PayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.commission3PayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Commission 3 Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col />
                </Row>
                <Row className="mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Taxes")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Taxes</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="taxes">
                      <Col>
                        <Form.Control
                          type="text"
                          name="taxes"
                          value={
                            editingField === "taxes"
                              ? formData.taxes
                              : formatAsCurrency(formData.taxes)
                          }
                          onFocus={(e) => {
                            setEditingField("taxes");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.taxes // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "Taxes"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.taxes ? "tbd-highlight" : ""}
                          placeholder="Enter Taxes"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col />
                  <Col />
                </Row>
                <Row className="even-row mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SetupAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Setup</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="setupAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="setupAmount"
                          value={
                            editingField === "setupAmount"
                              ? formData.setupAmount
                              : formatAsCurrency(formData.setupAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("setupAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.setupAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "SetupAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.setupAmount ? "tbd-highlight" : ""}
                          placeholder="Enter Setup Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="setupPayTo">
                      <Form.Control
                        type="text"
                        name="setupPayTo"
                        value={formData.setupPayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Setup Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "SetupPayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.setupPayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Setup Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="setupProviderQuote">
                      <Form.Control
                        type="text"
                        name="setupProviderQuote"
                        value={formData.setupProviderQuote}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Setup Provider Quote", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "SetupProviderQuote",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.setupProviderQuote
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Setup Provider Quote"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("StartAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Startup Cost</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="startAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="startAmount"
                          value={
                            editingField === "startAmount"
                              ? formData.startAmount
                              : formatAsCurrency(formData.startAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("startAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.startAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "StartAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.startAmount ? "tbd-highlight" : ""}
                          placeholder="Enter Start Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="startPayTo">
                      <Form.Control
                        type="text"
                        name="startPayTo"
                        value={formData.startPayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Start Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StartPayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.startPayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Start Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="startProviderQuote">
                      <Form.Control
                        type="text"
                        name="startProviderQuote"
                        value={formData.startProviderQuote}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Start Provider Quote", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StartProviderQuote",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.startProviderQuote
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Startup Provider Quote"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="even-row mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("CraneAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Crane</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="craneAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="craneAmount"
                          value={
                            editingField === "craneAmount"
                              ? formData.craneAmount
                              : formatAsCurrency(formData.craneAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("craneAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.craneAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "CraneAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.craneAmount ? "tbd-highlight" : ""}
                          placeholder="Enter Crane Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="cranePayTo">
                      <Form.Control
                        type="text"
                        name="cranePayTo"
                        value={formData.cranePayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Crane Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "CranePayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.cranePayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Crane Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="craneProviderQuote">
                      <Form.Control
                        type="text"
                        name="craneProviderQuote"
                        value={formData.craneProviderQuote}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Crane Provider Quote", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "CraneProviderQuote",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.craneProviderQuote
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Crane Provider Quote"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InspectionAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Inspection Service</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="inspectionAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="inspectionAmount"
                          value={
                            editingField === "inspectionAmount"
                              ? formData.inspectionAmount
                              : formatAsCurrency(formData.inspectionAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("inspectionAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.inspectionAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "InspectionAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.inspectionAmount ? "tbd-highlight" : ""}
                          placeholder="Enter Inspection Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="inspectionPayTo">
                      <Form.Control
                        type="text"
                        name="inspectionPayTo"
                        value={formData.inspectionPayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Inspection Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "InspectionPayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.inspectionPayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Inspection Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="inspectionProviderQuote">
                      <Form.Control
                        type="text"
                        name="inspectionProviderQuote"
                        value={formData.inspectionProviderQuote}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Inspection Provider Quote", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "InspectionProviderQuote",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.inspectionProviderQuote
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Inspection Provider Quote"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="even-row mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Warranty")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Warranty</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="warranty">
                      <Col>
                        <Form.Control
                          type="text"
                          name="warranty"
                          value={formData.warranty}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Warranty", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "Warranty",
                              tableKey,
                            })
                          }
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.warranty ? "tbd-highlight" : ""}
                          placeholder="Enter Warranty"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col />
                  <Col />
                </Row>
                <Row className="mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("OtherAmount")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Other</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="otherAmount">
                      <Col>
                        <Form.Control
                          type="text"
                          name="otherAmount"
                          value={
                            editingField === "otherAmount"
                              ? formData.otherAmount
                              : formatAsCurrency(formData.otherAmount)
                          }
                          onFocus={(e) => {
                            setEditingField("otherAmount");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.otherAmount // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "OtherAmount"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.otherAmount ? "tbd-highlight" : ""}
                          placeholder="Enter Other Amount"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="otherPayTo">
                      <Form.Control
                        type="text"
                        name="otherPayTo"
                        value={formData.otherPayTo}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Other Pay To", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "OtherPayTo",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || !canEditPriceTerms}
                        className={
                          zoneTbdFields?.otherPayTo ? "tbd-highlight" : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Other Pay To"
                      />
                    </Form.Group>
                  </Col>
                  <Col className="flex-grow-3">
                    <Form.Group controlId="otherProviderQuote">
                      <Form.Control
                        type="text"
                        name="otherProviderQuote"
                        value={formData.otherProviderQuote}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Other Provider Quote", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "OtherProviderQuote",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.otherProviderQuote
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={handleChange}
                        placeholder="Enter Other Provider Quote"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="even-row mb-3">
                  {" "}
                  <Col column="md" sm={1} className="form-label col-form-label col">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("NetRevenue")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Net Revenue</span>
                    </OverlayTrigger>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="netRevenue">
                      <Col>
                        <Form.Control
                          type="text"
                          name="netRevenue"
                          value={
                            editingField === "netRevenue"
                              ? formData.netRevenue
                              : formatAsCurrency(formData.netRevenue)
                          }
                          onFocus={(e) => {
                            setEditingField("netRevenue");
                            // Store the raw value, not the formatted value for currency fields
                            handleFocus({
                              ...e,
                              target: {
                                ...e.target,
                                value: formData.netRevenue // Use raw value from formData
                              }
                            });
                          }}
                          onBlur={(e) => {
                            setEditingField(null);
                            handleCurrencyBlur(
                              e,
                              handleBlur,
                              formData,
                              apiUrl,
                              tableKey,
                              "NetRevenue"
                            );
                          }}
                          onChange={handleChange}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.netRevenue ? "tbd-highlight" : ""}
                          placeholder="Enter Net Revenue"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col />
                  <Col />
                </Row>
              </Col>
            </Row>

            <Row className="price-breakdown-estimated-costs">
              <h4 className="subsection-header">
                Other Estimated Additional Costs (Paid by Others)
              </h4>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId="otherEstimatedFreight">
                    <Form.Label column="md" sm={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("OtherEstimatedFreight")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Freight</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        type="text"
                        name="otherEstimatedFreight"
                        value={
                          editingField === "otherEstimatedFreight"
                            ? formData.otherEstimatedFreight
                            : formatAsCurrency(formData.otherEstimatedFreight)
                        }
                        onFocus={(e) => {
                          setEditingField("otherEstimatedFreight");
                          // Store the raw value, not the formatted value for currency fields
                          handleFocus({
                            ...e,
                            target: {
                              ...e.target,
                              value: formData.otherEstimatedFreight // Use raw value from formData
                            }
                          });
                        }}
                        onBlur={(e) => {
                          setEditingField(null);
                          handleCurrencyBlur(
                            e,
                            handleBlur,
                            formData,
                            apiUrl,
                            tableKey,
                            "OtherEstimatedFreight"
                          );
                        }}
                        onChange={handleChange}
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.otherEstimatedFreight ? "tbd-highlight" : ""
                        }
                        placeholder="Enter Other Estimated Freight"
                      />
                    </Col>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group as={Row} controlId="otherEstimatedLocalLabor">
                    <Form.Label column="md" sm={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("OtherEstimatedLocalLabor")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Local Labor</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        type="text"
                        name="otherEstimatedLocalLabor"
                        value={
                          editingField === "otherEstimatedLocalLabor"
                            ? formData.otherEstimatedLocalLabor
                            : formatAsCurrency(formData.otherEstimatedLocalLabor)
                        }
                        onFocus={(e) => {
                          setEditingField("otherEstimatedLocalLabor");
                          // Store the raw value, not the formatted value for currency fields
                          handleFocus({
                            ...e,
                            target: {
                              ...e.target,
                              value: formData.otherEstimatedLocalLabor // Use raw value from formData
                            }
                          });
                        }}
                        onBlur={(e) => {
                          setEditingField(null);
                          handleCurrencyBlur(
                            e,
                            handleBlur,
                            formData,
                            apiUrl,
                            tableKey,
                            "OtherEstimatedLocalLabor"
                          );
                        }}
                        onChange={handleChange}
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.otherEstimatedLocalLabor ? "tbd-highlight" : ""
                        }
                        placeholder="Enter Other Estimated Local Labor"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId="otherEstimatedRental">
                    <Form.Label column="md" sm={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("OtherEstimatedRental")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Rental</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        type="text"
                        name="otherEstimatedRental"
                        value={
                          editingField === "otherEstimatedRental"
                            ? formData.otherEstimatedRental
                            : formatAsCurrency(formData.otherEstimatedRental)
                        }
                        onFocus={(e) => {
                          setEditingField("otherEstimatedRental");
                          // Store the raw value, not the formatted value for currency fields
                          handleFocus({
                            ...e,
                            target: {
                              ...e.target,
                              value: formData.otherEstimatedRental // Use raw value from formData
                            }
                          });
                        }}
                        onBlur={(e) => {
                          setEditingField(null);
                          handleCurrencyBlur(
                            e,
                            handleBlur,
                            formData,
                            apiUrl,
                            tableKey,
                            "OtherEstimatedRental"
                          );
                        }}
                        onChange={handleChange}
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.otherEstimatedRental ? "tbd-highlight" : ""
                        }
                        placeholder="Enter Other Estimated Rental"
                      />
                    </Col>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group as={Row} controlId="otherEstimatedOther">
                    <Form.Label column="md" sm={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("OtherEstimatedOther")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Other</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        type="text"
                        name="otherEstimatedOther"
                        value={
                          editingField === "otherEstimatedOther"
                            ? formData.otherEstimatedOther
                            : formatAsCurrency(formData.otherEstimatedOther)
                        }
                        onFocus={(e) => {
                          setEditingField("otherEstimatedOther");
                          // Store the raw value, not the formatted value for currency fields
                          handleFocus({
                            ...e,
                            target: {
                              ...e.target,
                              value: formData.otherEstimatedOther // Use raw value from formData
                            }
                          });
                        }}
                        onBlur={(e) => {
                          setEditingField(null);
                          handleCurrencyBlur(
                            e,
                            handleBlur,
                            formData,
                            apiUrl,
                            tableKey,
                            "OtherEstimatedOther"
                          );
                        }}
                        onChange={handleChange}
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={
                          zoneTbdFields?.otherEstimatedOther ? "tbd-highlight" : ""
                        }
                        placeholder="Enter Other Estimated Other"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
            </Row>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PriceBreakdown;
